// src/modules/agents/pages/AgentSettlement.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Typography, Table, Tag, Button, Row, Col, Statistic, Space, Select, Input,
  Modal, message, Divider, Spin, Tabs, DatePicker, Tooltip, Badge,
} from 'antd';
import {
  DollarOutlined, CheckCircleOutlined, TeamOutlined, FilterOutlined,
  ArrowLeftOutlined, BankOutlined, ClockCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getSettlementSummary, getAllCommissions, bulkMarkCommissionsPaid, markCommissionPaid,
} from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const COMMISSION_COLORS = { PENDING: 'orange', PAID: 'green' };

export default function AgentSettlement() {
  const navigate = useNavigate();

  // Settlement summary
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Commissions list
  const [commissions, setCommissions] = useState([]);
  const [commLoading, setCommLoading] = useState(false);
  const [commSummary, setCommSummary] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);

  // Bulk pay
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkPayModal, setBulkPayModal] = useState(false);
  const [payForm, setPayForm] = useState({ paymentMode: 'Cash', paymentReference: '', remarks: '' });
  const [paying, setPaying] = useState(false);

  // Single pay
  const [singlePayModal, setSinglePayModal] = useState({ open: false, commission: null });
  const [singlePayForm, setSinglePayForm] = useState({ paymentMode: 'Cash', paymentReference: '' });
  const [singlePaying, setSinglePaying] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await getSettlementSummary();
      setSummary(res.data);
    } catch {
      message.error('Failed to load settlement summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadCommissions = useCallback(async (page = 1, pageSize = 20) => {
    setCommLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (statusFilter) params.status = statusFilter;
      if (caseTypeFilter) params.caseType = caseTypeFilter;
      if (agentFilter) params.agentId = agentFilter;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].toISOString();
        params.endDate = dateRange[1].toISOString();
      }
      const res = await getAllCommissions(params);
      setCommissions(res.data || []);
      setCommSummary(res.summary || {});
      setPagination(p => ({ ...p, current: res.meta?.page || page, total: res.meta?.total || 0 }));
    } catch {
      message.error('Failed to load commissions');
    } finally {
      setCommLoading(false);
    }
  }, [statusFilter, caseTypeFilter, agentFilter, dateRange]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadCommissions(); }, [loadCommissions]);

  // Bulk pay handler
  const handleBulkPay = async () => {
    if (selectedRowKeys.length === 0) return;
    setPaying(true);
    try {
      const res = await bulkMarkCommissionsPaid({
        commissionIds: selectedRowKeys,
        ...payForm,
      });
      message.success(`${res.paidCount} commission(s) paid — Total: ${fmt(res.totalAmountPaid)}`);
      setBulkPayModal(false);
      setSelectedRowKeys([]);
      setPayForm({ paymentMode: 'Cash', paymentReference: '', remarks: '' });
      loadSummary();
      loadCommissions(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error(err.response?.data?.message || 'Bulk payment failed');
    } finally {
      setPaying(false);
    }
  };

  // Single pay handler
  const handleSinglePay = async () => {
    if (!singlePayModal.commission) return;
    setSinglePaying(true);
    try {
      await markCommissionPaid(singlePayModal.commission._id, singlePayForm);
      message.success('Commission marked as paid');
      setSinglePayModal({ open: false, commission: null });
      setSinglePayForm({ paymentMode: 'Cash', paymentReference: '' });
      loadSummary();
      loadCommissions(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setSinglePaying(false);
    }
  };

  // Agent summary columns
  const agentColumns = [
    {
      title: 'Agent ID', dataIndex: 'agentId', width: 110,
      render: (id, r) => <a onClick={() => navigate(`/agents/${r._id}`)}>{id}</a>,
    },
    { title: 'Name', dataIndex: 'agentName', width: 160 },
    { title: 'Phone', dataIndex: 'phone', width: 130 },
    {
      title: 'Commission %', dataIndex: 'defaultCommissionPercent', width: 110,
      render: (v) => `${v}%`,
    },
    {
      title: 'Pending Amount', dataIndex: 'pendingAmount', width: 150,
      render: (v) => <Text strong style={{ color: '#cf1322' }}>{fmt(v)}</Text>,
      sorter: (a, b) => a.pendingAmount - b.pendingAmount,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Pending Cases', dataIndex: 'pendingCount', width: 120,
      render: (v) => <Badge count={v} style={{ backgroundColor: '#faad14' }} />,
    },
    {
      title: 'Oldest Pending', dataIndex: 'oldestPending', width: 130,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Total Paid', dataIndex: 'totalEarnings', width: 130,
      render: (v) => <Text style={{ color: '#3f8600' }}>{fmt(v)}</Text>,
    },
    {
      title: 'Actions', width: 120, fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setAgentFilter(r._id); setActiveTab('commissions'); }}>
            View
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => navigate(`/agents/${r._id}`)}
          >
            Details
          </Button>
        </Space>
      ),
    },
  ];

  // Commission detail columns
  const commissionColumns = [
    {
      title: 'Commission ID', dataIndex: 'commissionId', width: 130,
    },
    {
      title: 'Agent', dataIndex: 'agentId', width: 160,
      render: (agent) => agent ? (
        <a onClick={() => navigate(`/agents/${agent._id}`)}>
          {agent.name} ({agent.agentId})
        </a>
      ) : '—',
    },
    {
      title: 'Case Type', dataIndex: 'caseType', width: 100,
      render: (v) => <Tag color={v === 'WALK_IN' ? 'blue' : 'purple'}>{v === 'WALK_IN' ? 'Walk-In' : 'Pledge'}</Tag>,
    },
    {
      title: 'Case #', dataIndex: 'caseNumber', width: 120,
      render: (v) => v || '—',
    },
    {
      title: 'Base Amount', dataIndex: 'baseAmount', width: 130,
      render: (v) => fmt(v),
    },
    {
      title: 'Commission %', dataIndex: 'commissionPercent', width: 110,
      render: (v) => `${v}%`,
    },
    {
      title: 'Commission Amount', dataIndex: 'commissionAmount', width: 150,
      render: (v) => <Text strong>{fmt(v)}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: (s) => <Tag color={COMMISSION_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Date', dataIndex: 'createdAt', width: 120,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Paid At', dataIndex: 'paidAt', width: 120,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Payment', dataIndex: 'paymentMode', width: 120,
      render: (v, r) => v ? (
        <Tooltip title={r.paymentReference || ''}>
          <Tag>{v}</Tag>
        </Tooltip>
      ) : '—',
    },
    {
      title: 'Action', width: 110, fixed: 'right',
      render: (_, record) => {
        if (record.status === 'PAID') return <Tag color="green">Paid</Tag>;
        return (
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => setSinglePayModal({ open: true, commission: record })}
          >
            Pay
          </Button>
        );
      },
    },
  ];

  const rowSelection = statusFilter === 'PENDING' ? {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({ disabled: record.status === 'PAID' }),
  } : undefined;

  const selectedTotal = commissions
    .filter(c => selectedRowKeys.includes(c._id))
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            <DollarOutlined /> Agent Payment Settlement
          </Title>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/agents')}>
            Back to Agents
          </Button>
        </div>
      </Card>

      {/* Summary Stats */}
      {summaryLoading ? (
        <Spin style={{ display: 'block', margin: '40px auto' }} />
      ) : summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Pending"
                value={fmt(summary.totals?.totalPending)}
                valueStyle={{ fontSize: 20, fontWeight: 600, color: '#cf1322' }}
                prefix={<ClockCircleOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {summary.totals?.pendingCount || 0} commission(s)
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Paid"
                value={fmt(summary.totals?.totalPaid)}
                valueStyle={{ fontSize: 20, fontWeight: 600, color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {summary.totals?.paidCount || 0} commission(s)
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Grand Total"
                value={fmt(summary.totals?.grandTotal)}
                valueStyle={{ fontSize: 20, fontWeight: 600 }}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Agents with Pending"
                value={summary.agents?.length || 0}
                valueStyle={{ fontSize: 20, fontWeight: 600, color: '#faad14' }}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'overview',
            label: (
              <span>
                <TeamOutlined /> Agent-wise Overview
                {summary?.agents?.length > 0 && (
                  <Badge count={summary.agents.length} style={{ marginLeft: 8, backgroundColor: '#faad14' }} />
                )}
              </span>
            ),
            children: (
              <Table
                dataSource={summary?.agents || []}
                columns={agentColumns}
                rowKey="_id"
                loading={summaryLoading}
                size="small"
                bordered
                scroll={{ x: 1200 }}
                pagination={{ pageSize: 10 }}
              />
            ),
          },
          {
            key: 'commissions',
            label: (
              <span>
                <DollarOutlined /> All Commissions
              </span>
            ),
            children: (
              <>
                {/* Filters */}
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col>
                    <Select
                      value={statusFilter || undefined}
                      onChange={v => { setStatusFilter(v || ''); setSelectedRowKeys([]); }}
                      placeholder="Status"
                      allowClear
                      style={{ width: 140 }}
                    >
                      <Select.Option value="PENDING">Pending</Select.Option>
                      <Select.Option value="PAID">Paid</Select.Option>
                    </Select>
                  </Col>
                  <Col>
                    <Select
                      value={caseTypeFilter || undefined}
                      onChange={v => setCaseTypeFilter(v || '')}
                      placeholder="Case Type"
                      allowClear
                      style={{ width: 140 }}
                    >
                      <Select.Option value="WALK_IN">Walk-In</Select.Option>
                      <Select.Option value="PLEDGE">Pledge</Select.Option>
                    </Select>
                  </Col>
                  <Col>
                    <RangePicker
                      onChange={v => setDateRange(v)}
                      style={{ width: 260 }}
                    />
                  </Col>
                  {agentFilter && (
                    <Col>
                      <Tag closable onClose={() => setAgentFilter('')} color="blue">
                        Filtered by Agent
                      </Tag>
                    </Col>
                  )}
                  <Col flex="auto" />
                  {selectedRowKeys.length > 0 && (
                    <Col>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => setBulkPayModal(true)}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Pay Selected ({selectedRowKeys.length}) — {fmt(selectedTotal)}
                      </Button>
                    </Col>
                  )}
                </Row>

                {/* Commission Totals for filtered view */}
                {commSummary.totalAmount > 0 && (
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col>
                      <Text type="secondary">
                        Filtered: <Text strong>{fmt(commSummary.totalAmount)}</Text> total
                        {' | '}
                        <Text style={{ color: '#faad14' }}>{fmt(commSummary.pendingAmount)}</Text> pending ({commSummary.pendingCount})
                        {' | '}
                        <Text style={{ color: '#52c41a' }}>{fmt(commSummary.paidAmount)}</Text> paid ({commSummary.paidCount})
                      </Text>
                    </Col>
                  </Row>
                )}

                <Table
                  dataSource={commissions}
                  columns={commissionColumns}
                  rowKey="_id"
                  loading={commLoading}
                  rowSelection={rowSelection}
                  size="small"
                  bordered
                  scroll={{ x: 1600 }}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} commissions`,
                    onChange: (p, s) => {
                      setPagination(prev => ({ ...prev, current: p, pageSize: s }));
                      loadCommissions(p, s);
                    },
                  }}
                />
              </>
            ),
          },
        ]} />
      </Card>

      {/* Bulk Pay Modal */}
      <Modal
        title={`Bulk Pay — ${selectedRowKeys.length} Commission(s)`}
        open={bulkPayModal}
        onOk={handleBulkPay}
        onCancel={() => setBulkPayModal(false)}
        confirmLoading={paying}
        okText="Confirm Bulk Payment"
        okButtonProps={{ style: { background: '#52c41a', borderColor: '#52c41a' } }}
      >
        <div style={{ padding: '12px 0' }}>
          <Row gutter={[16, 8]} style={{ marginBottom: 16, padding: 12, background: '#f6ffed', borderRadius: 6 }}>
            <Col span={12}>
              <Text strong>Commissions: </Text>
              <Text>{selectedRowKeys.length}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Total Amount: </Text>
              <Text style={{ color: '#3f8600', fontWeight: 600 }}>{fmt(selectedTotal)}</Text>
            </Col>
          </Row>
          <div style={{ marginBottom: 12 }}>
            <label>Payment Mode</label>
            <Select
              value={payForm.paymentMode}
              onChange={v => setPayForm(f => ({ ...f, paymentMode: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="Cash">Cash</Select.Option>
              <Select.Option value="Bank Transfer">Bank Transfer</Select.Option>
              <Select.Option value="UPI">UPI</Select.Option>
            </Select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Payment Reference (optional)</label>
            <Input
              value={payForm.paymentReference}
              onChange={e => setPayForm(f => ({ ...f, paymentReference: e.target.value }))}
              placeholder="e.g. UTR number, cheque no."
            />
          </div>
          <div>
            <label>Remarks (optional)</label>
            <Input.TextArea
              value={payForm.remarks}
              onChange={e => setPayForm(f => ({ ...f, remarks: e.target.value }))}
              placeholder="Any notes about this payment"
              rows={2}
            />
          </div>
        </div>
      </Modal>

      {/* Single Pay Modal */}
      <Modal
        title="Mark Commission as Paid"
        open={singlePayModal.open}
        onOk={handleSinglePay}
        onCancel={() => setSinglePayModal({ open: false, commission: null })}
        confirmLoading={singlePaying}
        okText="Confirm Payment"
      >
        {singlePayModal.commission && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Commission: </Text>
                  <Text>{singlePayModal.commission.commissionId}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Amount: </Text>
                  <Text style={{ color: '#3f8600', fontWeight: 600 }}>
                    {fmt(singlePayModal.commission.commissionAmount)}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text strong>Agent: </Text>
                  <Text>{singlePayModal.commission.agentId?.name || '—'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Case: </Text>
                  <Text>{singlePayModal.commission.caseNumber || '—'}</Text>
                </Col>
              </Row>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label>Payment Mode</label>
              <Select
                value={singlePayForm.paymentMode}
                onChange={v => setSinglePayForm(f => ({ ...f, paymentMode: v }))}
                style={{ width: '100%' }}
              >
                <Select.Option value="Cash">Cash</Select.Option>
                <Select.Option value="Bank Transfer">Bank Transfer</Select.Option>
                <Select.Option value="UPI">UPI</Select.Option>
              </Select>
            </div>
            <div>
              <label>Payment Reference (optional)</label>
              <Input
                value={singlePayForm.paymentReference}
                onChange={e => setSinglePayForm(f => ({ ...f, paymentReference: e.target.value }))}
                placeholder="e.g. UTR number, cheque no."
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
